'use client';

import { type ComponentPropsWithoutRef, type ElementRef, Fragment, forwardRef } from 'react';

import * as AccordionPrimitive from '@radix-ui/react-accordion';

import { cn } from '@/lib/utils';

import { IconStatic } from './icon/icon-static';

const Accordion = AccordionPrimitive.Root;

const AccordionItem = forwardRef<
    ElementRef<typeof AccordionPrimitive.Item>,
    ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
    <AccordionPrimitive.Item ref={ref} className={cn('border-b-1 first:border-t-1', className)} {...props} />
));
AccordionItem.displayName = 'AccordionItem';

type AccordionTriggerProps = {
    moreLabel?: string;
    lessLabel?: string;
    buttonsClassNames?: { label?: string; icon?: string; button?: string };
} & ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>;

const AccordionTrigger = forwardRef<ElementRef<typeof AccordionPrimitive.Trigger>, AccordionTriggerProps>(
    ({ className, children, ...props }, ref) => (
        <AccordionPrimitive.Header className='flex'>
            <AccordionPrimitive.Trigger
                ref={ref}
                className={cn(
                    'group flex w-full flex-1 items-center justify-between text-left transition-all [&[data-state=closed]_#less]:invisible [&[data-state=open]_#iconTrigger]:rotate-180 [&[data-state=open]_#more]:invisible',
                    className,
                )}
                {...props}>
                {children}
            </AccordionPrimitive.Trigger>
        </AccordionPrimitive.Header>
    ),
);
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

export const AccordionTriggerBtn = ({ buttonsClassNames, moreLabel, lessLabel }: AccordionTriggerProps) => (
    <div
        className={cn(
            'text-button-md-500 flex shrink-0 cursor-pointer items-center justify-center gap-2',
            buttonsClassNames?.button,
        )}>
        <div className={cn('hidden place-items-center md:grid', buttonsClassNames?.label)}>
            <span id='more' className={cn('col-start-1 row-start-1')}>
                {moreLabel}
            </span>
            <span id='less' className={cn('col-start-1 row-start-1')}>
                {lessLabel}
            </span>
        </div>
        <div className='bg-surface-gray-lightest flex size-7 items-center justify-center rounded-full'>
            <IconStatic
                id='iconTrigger'
                name='IcChevronDown'
                className={cn('size-4 shrink-0 transition-transform duration-200', buttonsClassNames?.icon)}
            />
        </div>
    </div>
);

const AccordionContent = forwardRef<
    ElementRef<typeof AccordionPrimitive.Content>,
    ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => {
    const wrapperClassName = 'text-body-md-400';
    const constainerClassName = 'pt-0 pb-8 md:pb-10';

    return (
        <>
            <AccordionPrimitive.Content
                className={cn(
                    wrapperClassName,
                    'overflow-hidden print:hidden',
                    'data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down',
                    className,
                )}
                {...props}>
                <div className={cn(constainerClassName, className)} ref={ref}>
                    {children}
                </div>
            </AccordionPrimitive.Content>

            <div
                className={cn(
                    wrapperClassName,
                    className,
                    'hidden print:flex print:overflow-auto! print:transition-none!',
                )}>
                <div className={cn(constainerClassName, className)}>{children}</div>
            </div>
        </>
    );
});
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
